import { Flex, Text } from '@radix-ui/themes'
import { formatLabel, formatValue } from './claim-form-utils'
import { ClaimantFields } from './claimant-fields'
import { ContactFields } from './contact-fields'
import { TicketFields } from './ticket-fields'
import { EditableClaimantFields } from './editable-claimant-fields'
import { EditableContactFields } from './editable-contact-fields'
import { EditableTicketFields, EditableTicketsData } from './editable-ticket-fields'
import { ClaimFormGeoDataResponse } from '@Generated/back-office-client/src'

export function FieldRow({
  label,
  value,
  className,
}: {
  label: string
  value: string | null
  className?: string
}) {
  return (
    <Flex direction="column" gap="1" className={`claim-form-field ${className ?? ''}`.trim()}>
      <Text size="1" color="gray">
        {label}
      </Text>
      <Text size="2">{value ?? '—'}</Text>
    </Flex>
  )
}

/** Render all fields of a flat object section */
export function ObjectFields({ data }: { data: Record<string, unknown> }) {
  return (
    <Flex direction="column" gap="2">
      {Object.entries(data).map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          return null
        }
        return <FieldRow key={key} label={formatLabel(key)} value={formatValue(value)} />
      })}
    </Flex>
  )
}

/** Render an array section (e.g., tickets) */
export function ArrayFields({ items, sectionKey }: { items: unknown[]; sectionKey: string }) {
  const singularLabel = sectionKey.replace(/s$/, '')
  return (
    <Flex direction="column" gap="3">
      {items.map((item, i) => {
        if (typeof item !== 'object' || item === null) {
          return (
            <FieldRow
              key={i}
              label={`${formatLabel(singularLabel)} ${i + 1}`}
              value={formatValue(item)}
            />
          )
        }
        return (
          <Flex key={i} direction="column" gap="2" className="claim-form-array-item">
            {items.length > 1 ? (
              <Text size="2" weight="bold">
                {formatLabel(singularLabel)} {i + 1}
              </Text>
            ) : null}
            <ObjectFields data={item as Record<string, unknown>} />
          </Flex>
        )
      })}
    </Flex>
  )
}

/** Render a section's content dynamically based on the data shape */
export function SectionContent({
  sectionKey,
  data,
  formData,
  schema,
  ticketIndex,
  onTicketIndexChange,
}: {
  sectionKey: string
  data: unknown
  formData?: Record<string, unknown>
  schema?: Record<string, unknown>
  ticketIndex?: number
  onTicketIndexChange?: (index: number) => void
}) {
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    if (sectionKey === 'claimant') {
      return <ClaimantFields data={data as Record<string, unknown>} />
    }
    if (sectionKey === 'contact') {
      return (
        <ContactFields data={data as Record<string, unknown>} formData={formData} schema={schema} />
      )
    }
  }
  if (sectionKey === 'tickets' && Array.isArray(data)) {
    return (
      <TicketFields
        tickets={data}
        paymentMethod={formData?.paymentMethod}
        schema={schema}
        currentIndex={ticketIndex}
        onIndexChange={onTicketIndexChange}
      />
    )
  }
  if (Array.isArray(data)) {
    return <ArrayFields items={data} sectionKey={sectionKey} />
  }
  if (typeof data === 'object' && data !== null) {
    return <ObjectFields data={data as Record<string, unknown>} />
  }
  return <FieldRow label={formatLabel(sectionKey)} value={formatValue(data)} />
}

/** Render editable content for a section */
export function EditableSectionContent({
  sectionKey,
  data,
  schema,
  geoData,
  formData,
  onChange,
  ticketIndex,
  onTicketIndexChange,
}: {
  sectionKey: string
  data: Record<string, unknown>
  schema: Record<string, unknown>
  geoData: ClaimFormGeoDataResponse
  formData?: Record<string, unknown>
  onChange: (updated: unknown) => void
  ticketIndex?: number
  onTicketIndexChange?: (index: number) => void
}) {
  if (sectionKey === 'claimant') {
    return (
      <EditableClaimantFields
        data={data}
        schema={schema}
        geoData={geoData}
        onChange={onChange as (updated: Record<string, unknown>) => void}
      />
    )
  }
  if (sectionKey === 'contact') {
    return (
      <EditableContactFields
        data={data}
        schema={schema}
        geoData={geoData}
        formData={formData}
        onChange={onChange as (updated: Record<string, unknown>) => void}
      />
    )
  }
  if (sectionKey === 'tickets') {
    return (
      <EditableTicketFields
        data={data as unknown as EditableTicketsData}
        schema={schema}
        onChange={onChange as (updated: EditableTicketsData) => void}
        currentIndex={ticketIndex}
        onIndexChange={onTicketIndexChange}
      />
    )
  }
  return <GenericEditableFields data={data} onChange={onChange} />
}

/** Fallback editable fields for unknown section types */
function GenericEditableFields({
  data,
  onChange,
}: {
  data: Record<string, unknown>
  onChange: (updated: unknown) => void
}) {
  return (
    <Flex direction="column" gap="2">
      {Object.entries(data).map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          return null
        }
        return (
          <Flex key={key} direction="column" gap="1" className="claim-form-field">
            <Text size="1" color="gray">
              {formatLabel(key)}
            </Text>
            <input
              type="text"
              value={String(value ?? '')}
              onChange={(e) => onChange({ ...data, [key]: e.target.value })}
              className="claim-form-generic-input"
            />
          </Flex>
        )
      })}
    </Flex>
  )
}
